/**
 * Step 7 (and a live room's step 4): Design the page.
 *
 * First the ask: two screenshots of pages the host likes (picture A shapes the
 * first page, picture B the next one, read for their layout only, the six lines
 * shown under each as soon as they are read), one line about how
 * the page should feel, and the colours, changed right here. "Design my page"
 * sends them, and the page designer writes the first page.
 *
 * Then the page: one real page, with Look closer and Use this page. "Make another"
 * makes one more, in a direction not used yet, with an optional note; every page
 * made stays to switch to and use.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DIRECTION_NAMES } from "../../../shared/board-copy";
import type { PageCandidate, PageInspirations, StructureBrief } from "../../../shared/types";
import { LookPickers, type LookMode } from "../../setup/parts";
import { lookName, lookOf, type Colours as BrandColours } from "../../setup/looks";
import "../../styles/look-picker.css";
import { Icon } from "../../components/ui/Icon";
import { cx } from "../../components/ui/cx";
import { api, ApiError } from "../../lib/api";
import { BRIEF_LINES } from "./briefLines";
import { pagesOf } from "./designPages";
import { StepHead } from "./MakeFrame";
import { ShotSlots } from "./ShotSlots";
import { useShotReads } from "./useShotReads";

const POLL_MS = 2000;
const SAVE_MS = 700;

// ── the colours, changed in place ───────────────────────────────────────────

function ColoursInPlace({ value, onChange }: { value: BrandColours; onChange: (next: BrandColours) => void }) {
  const [open, setOpen] = useState(false);
  // The same six sets and Custom as setup and Settings, from setup's own list.
  const [mode, setMode] = useState<LookMode>(() => lookOf(value));
  return (
    <div className={cx("nbcol", open && "on")}>
      <div className="nbcol-h">
        <Icon name="palette" />
        <span className="sp">
          Colours: <b>{lookName(value)}</b>
        </span>
        <button type="button" className="link" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {open ? "Done" : "Change"}
        </button>
      </div>
      {open ? (
        <div className="nbcol-b">
          <LookPickers
            mode={mode}
            colours={value}
            onPick={(m, colours) => {
              setMode(m);
              onChange(colours);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

// ── the ask ─────────────────────────────────────────────────────────────────

export interface DesignAsk {
  inspirations: PageInspirations;
  ask: string;
}

export function DesignAskCard({
  webinarId,
  n,
  of,
  live,
  colours,
  onColours,
  onStart,
  busy,
  error,
  initial,
}: {
  webinarId: string;
  n: number;
  of: number;
  live: boolean;
  colours: BrandColours;
  onColours: (next: BrandColours) => void;
  onStart: (ask: DesignAsk) => void;
  busy: boolean;
  error?: string;
  initial?: DesignAsk;
}) {
  const [inspirations, setInspirations] = useState<PageInspirations>(initial?.inspirations ?? {});
  const [ask, setAsk] = useState(initial?.ask ?? "");
  const { reads, reading, change, editLine } = useShotReads(webinarId, inspirations, setInspirations);
  return (
    <div className="card nbw">
      <StepHead n={n} of={of} title="Design the page" />
      <div className="nbdask">
        <ShotSlots value={inspirations} reads={reads} onChange={change} onEditLine={editLine} />
        <div className="field">
          <label htmlFor="nbfeel">
            What should the page feel like? <span className="muted b6">Optional</span>
          </label>
          <textarea dir="auto"
            id="nbfeel"
            className="inp"
            rows={3}
            maxLength={400}
            value={ask}
            onChange={(e) => setAsk(e.target.value)}
            placeholder={live ? "For example: warm and local, the date big, short and friendly." : "For example: calm and warm, lots of white space, the photo big."}
          />
        </div>
        <ColoursInPlace value={colours} onChange={onColours} />
      </div>
      {error ? (
        <p className="note warn mt16" role="alert">
          <Icon name="info" />
          <span>{error}</span>
        </p>
      ) : null}
      <div className="nbw-f">
        <button type="button" className="btn p lg" disabled={busy || reading} onClick={() => onStart({ inspirations, ask: ask.trim() })}>
          <Icon name="sparkle" /> {busy ? "Starting…" : reading ? "Reading the page…" : "Design my page"}
        </button>
      </div>
    </div>
  );
}

// ── a page drawn small ──────────────────────────────────────────────────────

/** A whole page document at its own width (`page`), scaled to fit the box, `height` tall. */
function PagePeek({ html, page = 1000, height = 470, title }: { html: string; page?: number; height?: number; title: string }) {
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.5);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => setScale(el.clientWidth ? el.clientWidth / page : 0.5);
    fit();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [page]);
  return (
    <div className="pvfit" ref={box} style={{ height }}>
      <div className="pvcanvas" style={{ width: page, height: height / scale, transform: `scale(${scale})` }}>
        <iframe title={title} srcDoc={html} sandbox="" tabIndex={-1} />
      </div>
    </div>
  );
}

// ── the pages ───────────────────────────────────────────────────────────────

function directionLine(c: PageCandidate | null): string {
  if (!c?.direction) return "";
  return DIRECTION_NAMES[c.direction] ?? "";
}

interface DesignChoiceProps {
  webinarId: string;
  n: number;
  of: number;
  live: boolean;
  feel: string;
  onChosen: () => void;
}

export function DesignChoice({ webinarId, n, of, live, feel, onChosen }: DesignChoiceProps) {
  const [pages, setPages] = useState<PageCandidate[]>([]);
  const [inspirations, setInspirations] = useState<PageInspirations>({});
  const [viewing, setViewing] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [closer, setCloser] = useState(false);
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const dirty = useRef(false);
  const saveTimer = useRef<number | null>(null);
  const saving = useRef<Promise<unknown>>(Promise.resolve());

  const load = useCallback(async () => {
    const got = await api.pages.list(webinarId);
    setPages(pagesOf(got.pairs));
    if (!dirty.current) setInspirations(got.inspirations);
    return got;
  }, [webinarId]);

  const newest = pages.length ? pages[pages.length - 1] : null;
  const shown = (viewing ? pages.find((p) => p.id === viewing) : null) ?? newest;
  const making = pages.some((p) => p.status === "making");
  // Why picture B: the first page was drawn from picture A, so the next one is the one picture B shapes.
  const next = pages.length === 1 ? inspirations.b : undefined;

  useEffect(() => {
    let alive = true;
    void load().catch(() => undefined);
    const timer = window.setInterval(() => {
      if (alive) void load().catch(() => undefined);
    }, POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    };
  }, [load]);

  const editLine = (key: keyof StructureBrief, line: string) => {
    const one = inspirations.b;
    if (!one?.brief) return;
    const updated: PageInspirations = { ...inspirations, b: { ...one, brief: { ...one.brief, [key]: line } } };
    setInspirations(updated);
    dirty.current = true;
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      saveTimer.current = null;
      saving.current = api.pages
        .inspirations(webinarId, { inspirations: updated })
        .catch(() => undefined)
        .finally(() => {
          dirty.current = false;
        });
    }, SAVE_MS);
  };

  const choose = async (c: PageCandidate | null) => {
    if (!c || c.status !== "ready" || picked) return;
    setPicked(c.id);
    setCloser(false);
    try {
      await api.pages.choose(webinarId, { id: c.id });
      // The chosen page has its moment before the board replaces the step.
      window.setTimeout(onChosen, 1200);
    } catch (e) {
      setPicked(null);
      setError(e instanceof ApiError ? e.message : "That page could not be used. Try again.");
    }
  };

  const another = async (useNote: boolean) => {
    const text = note.trim();
    setAsking(false);
    setError("");
    setViewing(null);
    try {
      // Why the lines go with the ask: a line typed a moment ago may still be waiting to save, and the run must
      // be written from the lines the host sees (specs/formal/inspiration-read).
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
      await saving.current;
      const body = { ...(useNote && text ? { note: text } : {}), ...(dirty.current ? { inspirations } : {}) };
      await api.pages.make(webinarId, Object.keys(body).length ? body : undefined);
      dirty.current = false;
      setNote("");
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Another page could not be started. Try again.");
    }
  };

  const heading = picked
    ? "Opening your board"
    : !shown || shown.status === "making"
      ? "Designing your page"
      : shown.status === "ready"
        ? "Your page is ready"
        : "The page did not land";
  const cardName = `Page ${shown ? pages.indexOf(shown) + 1 : 1}`;
  // With two or more pages the card's own title is the switch between them; with one it is just "Page 1".
  const cardTitle =
    !picked && pages.length > 1 ? (
      <div className="seg nbpages" role="radiogroup" aria-label="Pages made">
        {pages.map((p, i) => (
          <button key={p.id} type="button" role="radio" aria-checked={p.id === shown?.id} className={cx(p.id === shown?.id && "on")} onClick={() => setViewing(p.id)}>
            Page {i + 1}
            {p.status === "making" ? <span className="spin nbspin" /> : null}
          </button>
        ))}
      </div>
    ) : (
      <b>{cardName}</b>
    );

  const moreButton =
    !making && !picked ? (
      <button type="button" className="btn mk" onClick={() => setAsking((a) => !a)}>
        <Icon name="refresh" /> Make another
      </button>
    ) : null;

  const card = () => {
    const c = shown;
    if (!c || c.status === "making") {
      return (
        <div className="nbopt making">
          <div className="nbopt-h">
            {cardTitle}
            <span className="muted small">
              <span className="spin nbspin" /> Designing, this takes a few minutes
            </span>
          </div>
          <div className="nbsheet" aria-hidden="true">
            <i className="a" />
            <i className="b" />
            <i className="c" />
            <i className="d" />
            <i className="e" />
            <i className="f" />
          </div>
        </div>
      );
    }
    if (c.status === "failed" || !c.html) {
      return (
        <div className="nbopt">
          <div className="nbopt-h">
            {cardTitle}
            <span className="muted small">Not written</span>
          </div>
          <div className="nbsheet nbsheet-failed">
            <p className="small muted">{c.error || "This page could not be written."}</p>
          </div>
          {moreButton ? <div className="nbopt-f">{moreButton}</div> : null}
        </div>
      );
    }
    return (
      <div className={cx("nbopt", picked === c.id && "picked")}>
        <div className="nbopt-h">
          {cardTitle}
          <span className="muted small">{directionLine(c)}</span>
        </div>
        <button type="button" className="nbopt-pv" title="Look closer" onClick={() => setCloser(true)}>
          <PagePeek html={c.html} title={`${cardName}, small`} />
          <span className="nbopt-fade" />
        </button>
        <div className="nbopt-f">
          {moreButton}
          <button type="button" className="btn" onClick={() => setCloser(true)}>
            <Icon name="expand" /> Look closer
          </button>
          <button type="button" className="btn p" disabled={picked !== null} onClick={() => void choose(c)}>
            Use this page
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="card nbw nbdesign">
      <StepHead n={n} of={of} title={heading} />
      {feel && pages.length <= 1 ? (
        <div className="nbfelt">
          <Icon name="sparkle" />
          <span>You asked for: “{feel}”</span>
        </div>
      ) : null}
      <div className="nbone">{card()}</div>
      {asking && !making ? (
        <div className="nbaskbox">
          <b>What should the next one do differently?</b>
          <textarea dir="auto"
            className="inp"
            rows={2}
            maxLength={400}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={live ? "Optional. Warmer, shorter, the time bigger, lead with the questions…" : "Optional. Warmer, shorter, lead with the offer…"}
            autoFocus
          />
          {next?.brief ? (
            <details className="nbbrief2">
              <summary>
                <Icon name="pic" />
                <span className="sp">Layout from picture B</span>
                <Icon name="down" />
              </summary>
              <div className="nbbrief2-l">
                {BRIEF_LINES.map((b) => (
                  <label key={b.key}>
                    <span>{b.label}</span>
                    <input dir="auto" className="inp" value={next.brief?.[b.key] ?? ""} onChange={(e) => editLine(b.key, e.target.value)} />
                  </label>
                ))}
              </div>
            </details>
          ) : null}
          <div className="row g8">
            <button type="button" className="btn q" onClick={() => void another(false)}>
              Skip, just make another
            </button>
            <button type="button" className="btn p" onClick={() => void another(true)}>
              Make another
            </button>
          </div>
        </div>
      ) : null}
      {error ? (
        <p className="note warn mt16" role="alert">
          <Icon name="info" />
          <span>{error}</span>
        </p>
      ) : null}
      {closer && shown?.html ? (
        <LookCloser name={cardName} html={shown.html} onClose={() => setCloser(false)} onUse={() => void choose(shown)} />
      ) : null}
    </div>
  );
}

/** One design at full size, on a laptop or in a phone frame. */
function LookCloser({ name, html, onClose, onUse }: { name: string; html: string; onClose: () => void; onUse: () => void }) {
  const [phone, setPhone] = useState(false);
  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);
  return createPortal(
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sheet nbwide" role="dialog" aria-modal="true" aria-label={`${name}, full size`}>
        <div className="nbpv-h">
          <b>{name}</b>
          <span className="sp" />
          <div className="seg" role="radiogroup" aria-label="Screen">
            <button type="button" role="radio" aria-checked={!phone} className={cx(!phone && "on")} onClick={() => setPhone(false)}>
              <Icon name="monitor" /> Desktop
            </button>
            <button type="button" role="radio" aria-checked={phone} className={cx(phone && "on")} onClick={() => setPhone(true)}>
              <Icon name="phone" /> Phone
            </button>
          </div>
          <button type="button" className="btn p" onClick={onUse}>
            Use this page
          </button>
          <button type="button" className="ibtn" aria-label="Close" onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>
        <div className={cx("nbpv-b", phone && "phone")}>
          {phone ? (
            <div className="nbphone">
              <div className="nbphone-in">
                <iframe className="nbpv-frame" title={`${name} on a phone`} srcDoc={html} sandbox="" />
              </div>
            </div>
          ) : (
            <iframe className="nbpv-frame nbpv-wide" title={`${name} on a laptop`} srcDoc={html} sandbox="" />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

